import {
  EQUIPMENT,
  EXERCISE_CATEGORIES,
  EXERCISE_DIFFICULTIES,
  MUSCLE_GROUPS,
  type AdminExerciseListItem,
} from '@steado/admin-contract/exercises';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ErrorState } from '@/components/shell/ErrorState';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { PaginationFooter } from '@/components/table/PaginationFooter';
import { ScopePills } from '@/components/table/ScopePills';
import { TableToolbar, type FilterSpec } from '@/components/table/TableToolbar';
import { BadgeCell, ChipsCell, TextCell } from '@/components/table/cells';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { scopeCountsQueryOptions } from '@/lib/scope-counts-query';
import { humanise } from '@/lib/format';

import { EXERCISES_PAGE_SIZE, exercisesQueryOptions } from './exercises-query';
import type { ExercisesSearch } from './exercises-search';

const COLUMNS: readonly TableColumn[] = [
  { key: 'exercise', label: 'Movement', width: '1.7fr' },
  { key: 'category', label: 'Category', width: '0.8fr' },
  { key: 'difficulty', label: 'Difficulty', width: '1fr' },
  { key: 'muscles', label: 'Trains', width: '1.2fr' },
  { key: 'equipment', label: 'Needs', width: '1.1fr' },
  { key: 'clip', label: 'Demo', width: '0.85fr' },
  { key: 'added', label: 'Added', width: '0.8fr', align: 'right' },
];

/**
 * Difficulty is a safety-adjacent read, not decoration: `advanced` is the value
 * that removes a movement from every beginner's plan, so it gets the tone that
 * makes a reviewer look twice.
 */
const DIFFICULTY_TONE = {
  beginner: 'ok',
  intermediate: 'mute',
  advanced: 'warn',
} as const;

export interface ExercisesScreenProps {
  readonly search: ExercisesSearch;
  readonly onSearchChange: (next: ExercisesSearch) => void;
  /**
   * Opening an exercise is a navigation to its own page, not a filter change,
   * so it comes in from the route component and this screen stays
   * presentational.
   */
  readonly onOpenExercise: (exerciseId: string) => void;
  /** Creating one is a page now, not a dialog over the table. */
  readonly onNewExercise: () => void;
}

/**
 * How the movement behaves, as the chips the design shows.
 *
 * Null is unassessed and contributes nothing: an exercise nobody has ruled on
 * and one deliberately marked "no axial load" must not read the same, and an
 * empty cell is the honest rendering of the first.
 */
function behaviourOf(exercise: AdminExerciseListItem): string[] {
  return [
    exercise.mechanic,
    exercise.movementPattern,
    exercise.laterality,
    exercise.impact === null ? null : `${exercise.impact} impact`,
    exercise.axialLoad === null ? null : exercise.axialLoad ? 'axial load' : 'no axial load',
  ]
    .filter((value): value is string => value !== null)
    .map(humanise);
}

/** How many demonstration stills the row carries, or that it carries none. */
function stillsOf(exercise: AdminExerciseListItem): string {
  const count = exercise.images.length;
  return count === 0 ? 'no stills' : `${String(count)} still${count === 1 ? '' : 's'}`;
}

export function ExercisesScreen({
  search,
  onSearchChange,
  onOpenExercise,
  onNewExercise,
}: ExercisesScreenProps) {
  const { api, can } = useAuth();
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);

  const query = useQuery(exercisesQueryOptions(api, search));
  const counts = useQuery(scopeCountsQueryOptions(api));

  const setFilter = useCallback(
    (patch: Partial<ExercisesSearch>) => {
      setCursorStack([]);
      onSearchChange({ ...search, ...patch, cursor: undefined });
    },
    [search, onSearchChange],
  );

  const onNext = useCallback(() => {
    const next = query.data?.pagination.nextCursor;
    if (next === undefined || next === null) return;
    setCursorStack((stack) => [...stack, search.cursor]);
    onSearchChange({ ...search, cursor: next });
  }, [query.data, search, onSearchChange]);

  const onPrevious = useCallback(() => {
    setCursorStack((stack) => {
      onSearchChange({ ...search, cursor: stack[stack.length - 1] });
      return stack.slice(0, -1);
    });
  }, [search, onSearchChange]);

  const filterSpecs: readonly FilterSpec[] = useMemo(
    () => [
      {
        id: 'category',
        label: 'Category',
        value: search.category,
        options: EXERCISE_CATEGORIES.map((value) => ({ value, label: value })),
        onChange: (category) => {
          setFilter({ category: category as ExercisesSearch['category'] });
        },
      },
      {
        id: 'difficulty',
        label: 'Difficulty',
        value: search.difficulty,
        options: EXERCISE_DIFFICULTIES.map((value) => ({ value, label: value })),
        onChange: (difficulty) => {
          setFilter({ difficulty: difficulty as ExercisesSearch['difficulty'] });
        },
      },
      {
        id: 'source',
        label: 'Source',
        value: search.source,
        options: [
          { value: 'curated', label: 'curated' },
          { value: 'imported', label: 'imported' },
        ],
        onChange: (source) => {
          setFilter({ source: source as ExercisesSearch['source'] });
        },
      },
      {
        id: 'muscle',
        label: 'Muscle',
        value: search.muscle,
        options: MUSCLE_GROUPS.map((value) => ({ value, label: humanise(value) })),
        onChange: (muscle) => {
          setFilter({ muscle: muscle as ExercisesSearch['muscle'] });
        },
      },
      {
        id: 'equipment',
        label: 'Equipment',
        value: search.equipment,
        options: EQUIPMENT.map((value) => ({ value, label: humanise(value) })),
        onChange: (equipment) => {
          setFilter({ equipment: equipment as ExercisesSearch['equipment'] });
        },
      },
      {
        id: 'media',
        label: 'Clip',
        value: search.media,
        options: [
          { value: 'with', label: 'has a clip' },
          { value: 'without', label: 'needs filming' },
        ],
        onChange: (media) => {
          setFilter({ media: media as ExercisesSearch['media'] });
        },
      },
      {
        id: 'activity',
        label: 'State',
        value: search.activity,
        options: [
          { value: 'active', label: 'active' },
          { value: 'inactive', label: 'deactivated' },
        ],
        onChange: (activity) => {
          setFilter({ activity: activity as ExercisesSearch['activity'] });
        },
      },
    ],
    [search, setFilter],
  );

  const rows: readonly TableRow[] = useMemo(
    () =>
      (query.data?.items ?? []).map((exercise) => ({
        id: exercise.id,
        onOpen: () => {
          onOpenExercise(exercise.id);
        },
        cells: [
          <TextCell
            key="exercise"
            value={exercise.name}
            /*
             * Category and provenance share the sub-line: both are one word,
             * both are things you filter by rather than read, and giving each
             * its own column cost the width that "Unsafe on" now uses.
             */
            sub={`${exercise.category} · ${exercise.sourceId === null ? 'curated' : 'imported'}${
              exercise.isActive ? '' : ' · deactivated'
            }`}
            weight="medium"
          />,
          <BadgeCell
            key="difficulty"
            label={exercise.difficulty}
            tone={DIFFICULTY_TONE[exercise.difficulty]}
          />,
          <ChipsCell key="muscles" items={exercise.primaryMuscles.map(humanise)} max={3} />,
          <ChipsCell key="equipment" items={exercise.equipment.map(humanise)} max={2} />,
          /*
           * The joints this movement loads enough to be unsafe on. It is what
           * an injury restriction reads, so a curator reviewing the library
           * needs it in the scan rather than one screen deeper.
           */
          exercise.contraindications.length === 0 ? (
            <TextCell key="unsafe" value={null} />
          ) : (
            <ChipsCell key="unsafe" items={exercise.contraindications.map(humanise)} max={2} />
          ),
          <ChipsCell key="behaviour" items={behaviourOf(exercise)} max={2} />,
          exercise.media === null ? (
            // "needs filming" rather than an em dash: this column exists to be
            // scanned for gaps, and a blank reads as missing data.
            <TextCell key="clip" value="needs filming" tone="warn" weight="medium" sub={stillsOf(exercise)} />
          ) : (
            <TextCell key="clip" value="filmed" weight="medium" sub={stillsOf(exercise)} />
          ),
        ],
      })),
    [query.data, onOpenExercise],
  );

  const isFiltered =
    search.search !== undefined ||
    search.category !== undefined ||
    search.difficulty !== undefined ||
    search.muscle !== undefined ||
    search.equipment !== undefined ||
    search.media !== undefined ||
    search.source !== undefined ||
    search.activity !== undefined;

  /**
   * The import lands ~700 rows inactive at once, which is a queue rather than a
   * library. One click puts an operator in front of exactly the rows nobody has
   * ruled on yet; activation stays per-exercise, from the detail screen.
   */
  const isReviewQueue = search.source === 'imported' && search.activity === 'inactive';

  const onSelectScope = useCallback(
    (scope: 'all' | 'review') => {
      setCursorStack([]);
      onSearchChange(scope === 'review' ? { source: 'imported', activity: 'inactive' } : {});
    },
    [onSearchChange],
  );

  const onReset = useCallback(() => {
    setCursorStack([]);
    onSearchChange({});
  }, [onSearchChange]);

  const pageOffset = cursorStack.length * EXERCISES_PAGE_SIZE;

  if (query.isError) {
    return (
      <ErrorState
        title="Could not load the library"
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <ScopePills
        label="Library scope"
        scopes={[
          {
            id: 'all',
            label: 'The library',
            ...(counts.data === undefined ? {} : { count: counts.data.exercises.active }),
          },
          {
            id: 'review',
            label: 'Needs review',
            ...(counts.data === undefined ? {} : { count: counts.data.exercises.review }),
          },
        ]}
        value={isReviewQueue ? 'review' : 'all'}
        onChange={onSelectScope}
      />

      <TableToolbar
        searchValue={search.search ?? ''}
        searchPlaceholder="Search name or slug…"
        onSearchChange={(value) => {
          setFilter({ search: value === '' ? undefined : value });
        }}
        filters={filterSpecs}
        isFiltered={isFiltered}
        onReset={onReset}
        action={
          <Button
            type="button"
            size="sm"
            onClick={onNewExercise}
            disabled={!can.mutate}
            title={can.mutate ? undefined : 'Creating an exercise requires the admin role.'}
          >
            New exercise
          </Button>
        }
      />

      <DataTable
        caption="Exercises"
        title={isReviewQueue ? 'Needs review' : 'The library'}
        count={rows.length === 0 ? undefined : `showing ${String(rows.length)}`}
        columns={COLUMNS}
        rows={rows}
        isLoading={query.isPending}
        emptyTitle={isFiltered ? 'No matches' : 'No exercises'}
        emptyBody={
          isFiltered
            ? 'No exercise matches the current search and filters. Clear them to see the library.'
            : 'The library is empty. Seed it, or create the first exercise.'
        }
        emptyAction={
          isFiltered ? (
            <Button variant="outline" onClick={onReset}>
              Clear filters
            </Button>
          ) : undefined
        }
        footer={
          <PaginationFooter
            rangeStart={rows.length === 0 ? 0 : pageOffset + 1}
            rangeEnd={pageOffset + rows.length}
            hasPrevious={cursorStack.length > 0}
            hasNext={query.data?.pagination.hasMore ?? false}
            onPrevious={onPrevious}
            onNext={onNext}
            isLoading={query.isPending}
          />
        }
      />
    </div>
  );
}
